import React from 'react';
import { Eye, Loader2, Check, AlertTriangle, XCircle } from 'lucide-react';

/**
 * What a reviewer sees when the film has been watched against its own script.
 *
 * ⚠️ EVERY FINDING SHOWS THE FRAMES IT IS ABOUT. A finding you cannot see is an
 * assertion, and this one is made by a model looking at pictures - the reader
 * has to be able to disagree with it in one glance.
 *
 * ⚠️ "COULD NOT CHECK" IS A THIRD ANSWER AND IT IS RENDERED DIFFERENTLY. A
 * review that failed to look at anything must never read as "no problems found".
 * That is the failure the Media Intake sweep shipped with, where a dead API key
 * produced a confident all-clear.
 *
 * ⚠️ IT CHANGES NOTHING. Every fix is a button somebody presses on the footage
 * grid, because a picture that does not literally match the words is sometimes
 * exactly what a cutaway is for.
 */

const VERDICT = {
  good: { label: 'Fine', color: '#1a7f37', bg: '#f2fbf5', border: '#cde8d5', Icon: Check },
  minor: { label: 'Worth a look', color: '#b25e09', bg: '#fff8ee', border: '#f0c48a', Icon: AlertTriangle },
  problem: { label: 'Wrong', color: '#c0392b', bg: '#fdecec', border: '#f3c4c0', Icon: XCircle },
};

export default function FilmReview({ review, findings = [], busy, onRun, onFixShot }) {
  const running = review && ['queued', 'running'].includes(review.status);
  const bad = findings.filter((f) => f.verdict !== 'good');
  const shown = bad.length ? bad : findings;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Check the film</span>
        <button className="btn btn-primary" disabled={busy || running} onClick={onRun}>
          <Eye size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
          {running ? 'Watching…' : review ? 'Watch it again' : 'Watch the film'}
        </button>
      </div>
      <div className="card-body">
        {!review ? (
          <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0, color: 'var(--gray-700)' }}>
            This watches the rendered film and reads each shot against the line being spoken over it.
            It looks for a picture that contradicts the words, a subject that never appears, gibberish
            signage, letterboxing or film-strip borders the model drew in, and modern objects in a
            historical scene. None of those are things the render checks can see.
          </p>
        ) : null}

        {running ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--navy)' }}>
            <Loader2 size={14} className="spin" />
            Taking three frames from every shot and reading each against its line. A minute or two.
          </div>
        ) : null}

        {review && review.status === 'failed' ? (
          <div style={{ background: '#fdecec', color: '#c0392b', padding: '10px 12px', borderRadius: 5, fontSize: 12 }}>
            {review.last_error || 'The review failed.'}
          </div>
        ) : null}

        {review && review.status === 'done' ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 14, lineHeight: 1.6 }}>
              Watched <strong>{review.shots_reviewed}</strong> shot{review.shots_reviewed === 1 ? '' : 's'}.{' '}
              {bad.length
                ? <><strong style={{ color: '#b25e09' }}>{bad.length}</strong> {bad.length === 1 ? 'is' : 'are'} worth looking at.</>
                : 'Every shot matches what is being said over it.'}
              {review.shots_failed ? (
                <span style={{ color: '#c0392b' }}>
                  {' '}<strong>{review.shots_failed}</strong> could not be checked
                  {review.last_error ? ` (${review.last_error})` : ''}, so this is not a clean bill of health.
                </span>
              ) : null}
            </div>

            {shown.map((f) => {
              const v = VERDICT[f.verdict] || VERDICT.minor;
              return (
                <div key={f.shot_number} style={{
                  border: `1px solid ${v.border}`, background: v.bg, borderRadius: 6,
                  padding: '10px 12px', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                    <v.Icon size={13} color={v.color} />
                    <strong style={{ fontSize: 12, color: 'var(--navy)' }}>Shot {f.shot_number}</strong>
                    <span style={{ fontSize: 11, fontWeight: 700, color: v.color }}>{v.label}</span>
                    {f.subject_present === 'late' ? (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#b25e09', background: '#fff', padding: '1px 6px', borderRadius: 8, border: '1px solid #f0c48a' }}>
                        ARRIVES LATE
                      </span>
                    ) : null}
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--gray-500)', fontStyle: 'italic', marginBottom: 7 }}>
                    &ldquo;{f.script_text}&rdquo;
                  </div>

                  {(f.frame_keys || []).length ? (
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      {f.frame_keys.map((k, i) => (
                        <img key={k} src={`/.netlify/functions/r2-image?key=${encodeURIComponent(k)}`}
                          alt={`Shot ${f.shot_number}, ${['start', 'middle', 'end'][i] || ''}`} loading="lazy"
                          style={{ width: '33%', aspectRatio: '16/9', objectFit: 'cover', borderRadius: 4, display: 'block', border: '1px solid rgba(0,0,0,.1)' }} />
                      ))}
                    </div>
                  ) : null}

                  {f.finding ? (
                    <div style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.6 }}>{f.finding}</div>
                  ) : null}
                  {f.text_in_frame ? (
                    <div style={{ fontSize: 11, color: '#b25e09', marginTop: 4 }}>
                      Words visible in the picture: &ldquo;{f.text_in_frame}&rdquo;
                    </div>
                  ) : null}
                  {f.fix ? (
                    <div style={{ fontSize: 12, color: 'var(--navy)', marginTop: 6 }}>
                      <strong>What to do:</strong> {f.fix}
                    </div>
                  ) : null}

                  {f.verdict !== 'good' && onFixShot ? (
                    <button className="btn btn-outline" disabled={busy}
                      style={{ fontSize: 10, padding: '3px 9px', marginTop: 8 }}
                      onClick={() => onFixShot(f.shot_number)}>
                      Go to shot {f.shot_number}
                    </button>
                  ) : null}
                </div>
              );
            })}

            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
              This changes nothing on its own. A picture that does not literally match the words is
              sometimes exactly what a cutaway is for, so every fix stays your call.
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
